import { Routes, Route, Navigate } from 'react-router-dom'
import { useHideAddressBar } from '@/hooks/useHideAddressBar'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { authService } from '@/services/authService'

// Partner Pages Only
import PartnerLogin from '@/pages/partner/PartnerLogin'
import PartnerDashboard from '@/pages/partner/PartnerDashboard'
import PartnerUsers from '@/pages/partner/PartnerUsers'
import PartnerTrading from '@/pages/partner/PartnerTrading'
import PartnerRealtime from '@/pages/partner/PartnerRealtime'
import PartnerDeposits from '@/pages/partner/PartnerDeposits'
import PartnerWithdrawals from '@/pages/partner/PartnerWithdrawals'
import PartnerMyWithdrawals from '@/pages/partner/PartnerMyWithdrawals'
import PartnerStatistics from '@/pages/partner/PartnerStatistics'
import PartnerInquiries from '@/pages/partner/PartnerInquiries'

// Layout
import PartnerLayout from '@/layouts/PartnerLayout'

// 인증 확인을 위한 컴포넌트
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('common')
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  useEffect(() => {
    let isCancelled = false // cleanup flag
    
    const checkAuth = async () => {
      try {
        console.log('🔍 ProtectedRoute checkAuth 시작')
        
        // localStorage에 토큰이 있는지 먼저 확인
        const token = localStorage.getItem('partner_access_token')
        console.log('🔍 localStorage 토큰 확인:', token ? `${token.substring(0, 50)}...` : 'null')
        
        if (!token) {
          console.log('❌ 토큰이 없어서 로그인으로 리다이렉트')
          if (!isCancelled) setIsAuthenticated(false)
          return
        }

        // authService를 사용하여 토큰과 함께 요청
        console.log('🔍 파트너 프로필 요청 시작')
        await authService.getCurrentPartner()
        console.log('✅ 파트너 인증 성공')
        if (!isCancelled) setIsAuthenticated(true)
      } catch (error) {
        console.log('❌ 파트너 인증 실패:', error)
        if (!isCancelled) setIsAuthenticated(false)
      }
    }

    checkAuth()
    
    // cleanup function
    return () => {
      isCancelled = true
    }
  }, [])

  if (isAuthenticated === null) {
    // 로딩 중
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-white">{t('loading')}</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // 인증되지 않은 경우 로그인 페이지로 리다이렉트
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AppPartner() {
  // 모바일에서 주소창 숨기기
  useHideAddressBar()

  return (
    <div className="bg-dark-950 overflow-hidden h-app" style={{
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
      paddingLeft: 'env(safe-area-inset-left)',
      paddingRight: 'env(safe-area-inset-right)'
    }}>
      <Routes>
        {/* Partner Login */}
        <Route path="/login" element={<PartnerLogin />} />

        {/* Partner routes (nested) - Protected */}
        <Route path="/" element={<ProtectedRoute><PartnerLayout /></ProtectedRoute>}>
          <Route index element={<PartnerDashboard />} />
          <Route path="dashboard" element={<PartnerDashboard />} />
          <Route path="users" element={<PartnerUsers />} />
          <Route path="trading" element={<PartnerTrading />} />
          <Route path="realtime" element={<PartnerRealtime />} />
          <Route path="deposits" element={<PartnerDeposits />} />
          <Route path="withdrawals" element={<PartnerWithdrawals />} />
          <Route path="my-withdrawals" element={<PartnerMyWithdrawals />} />
          <Route path="statistics" element={<PartnerStatistics />} />
          <Route path="inquiries" element={<PartnerInquiries />} />
        </Route>

        {/* Partner routes with /partner prefix - Protected */}
        <Route path="/partner" element={<ProtectedRoute><PartnerLayout /></ProtectedRoute>}>
          <Route index element={<PartnerDashboard />} />
          <Route path="dashboard" element={<PartnerDashboard />} />
          <Route path="users" element={<PartnerUsers />} />
          <Route path="trading" element={<PartnerTrading />} />
          <Route path="realtime" element={<PartnerRealtime />} />
          <Route path="deposits" element={<PartnerDeposits />} />
          <Route path="withdrawals" element={<PartnerWithdrawals />} />
          <Route path="my-withdrawals" element={<PartnerMyWithdrawals />} />
          <Route path="statistics" element={<PartnerStatistics />} />
          <Route path="inquiries" element={<PartnerInquiries />} />
        </Route>

        {/* Catch all - redirect to partner dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </div>
  )
}

export default AppPartner